import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import useTeamCompetitionData from './useTeamCompetitionData';
import { slugify } from '../utils/slugify'; 
import { Helmet } from 'react-helmet-async';

const mockTiles = [
  { title: 'Szanse w grupach', description: 'Szanse na konkretne miejsca', link: (id) => `/liga/431/wcPositionMatrix` },
  { title: 'Ranking ELO', description: '...i jego zmiany', link: (id) => `/liga/431/elo-ranking` },  
  { title: 'PODSUMOWANIE', description: 'Podsumowanie fazy pucharowej', link: (id) => `/liga/431/wcSummary` },  
  { title: 'Terminarz', description: 'Lista meczów', link: (id) => `/liga/431/fixtures` },
  { title: 'Trzecie miejsca', description: 'Szanse na awans z 3. miejsca', link: (id) => `/liga/431/WcPointThreshold` },
  { title: 'Własne symulacje', description: 'Stwórz symulację warunkową', link: (id) => `/liga/431/WcYourSimulation` },
];

const GROUPS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const WcLeaguePage = () => {
  const id = 431;
  const [selectedGroup, setSelectedGroup] = useState('A');

  // Pobranie danych o rozgrywkach przy pomocy hooka
  const competitionData = useTeamCompetitionData(id);
  
  // Bezpieczne wyciągnięcie listy meczów z uwzględnieniem głębszych struktur
  const matches = useMemo(() => {
    if (!competitionData) return [];
    if (Array.isArray(competitionData)) return competitionData;
    
    // Sprawdzamy różne warianty zagnieżdżenia obiektów z API
    if (competitionData.matches && Array.isArray(competitionData.matches)) return competitionData.matches;
    if (competitionData.data && Array.isArray(competitionData.data)) return competitionData.data;
    if (competitionData.data && competitionData.data.matches && Array.isArray(competitionData.data.matches)) return competitionData.data.matches;
    if (competitionData.fixtures && Array.isArray(competitionData.fixtures)) return competitionData.fixtures;
    
    return [];
  }, [competitionData]);

  // Funkcja obliczająca tabelę dla wybranej grupy "w locie"
  const groupTable = useMemo(() => {
   
    
    if (!matches || matches.length === 0) return [];

    const tableMap = {};

    const initTeamRow = (teamId, teamName) => {
      if (!tableMap[teamId]) {
        tableMap[teamId] = {
          teamId,
          teamName: teamName || `Drużyna ${teamId}`,
          matchesPlayed: 0,
          points: 0,
          goalsScored: 0,
          goalsConceded: 0,
        };
      }
    };

    // Filtrowanie meczów należących do wybranej grupy przy użyciu dostępnych pól
    const filteredMatches = matches.filter(m => {
      const mRound = m.round ? m.round.toString().toUpperCase() : '';
      const mGroup = m.group ? m.group.toString().toUpperCase() : '';
      const mGroupName = m.groupName ? m.groupName.toString().toUpperCase() : '';
      const mStage = m.stage ? m.stage.toString().toUpperCase() : '';

      const targetGroupStr = `GRUPA ${selectedGroup}`;

      return (
        mRound === targetGroupStr || mRound === selectedGroup || mRound.endsWith(` ${selectedGroup}`) ||
        mGroup === targetGroupStr || mGroup === selectedGroup || mGroup.endsWith(` ${selectedGroup}`) ||
        mGroupName === targetGroupStr || mGroupName === selectedGroup || mGroupName.endsWith(` ${selectedGroup}`) ||
        mStage === targetGroupStr || mStage === selectedGroup || mStage.endsWith(` ${selectedGroup}`)
      );
    });

    console.log(`Wybrana grupa w UI: ${selectedGroup}`);
    console.log(`Liczba meczów dopasowanych do grupy ${selectedGroup}:`, filteredMatches.length);
    if (filteredMatches.length > 0) {
      console.log(`Przykładowy dopasowany mecz dla grupy ${selectedGroup}:`, filteredMatches[0]);
    }
    console.log("=========================================");

    // 1. Inicjalizacja wszystkich drużyn i kalkulacja tabeli bazowej (całkowitej)
    filteredMatches.forEach(m => {
      const homeId = m.homeTeamId;
      const homeName = m.homeTeamName || (m.homeTeam && m.homeTeam.name);
      const awayId = m.awayTeamId;
      const awayName = m.awayTeamName || (m.awayTeam && m.awayTeam.name);

      if (!homeId || !awayId) return;

      initTeamRow(homeId, homeName);
      initTeamRow(awayId, awayName);

      const isPlayed = (m.status || '').toLowerCase() === 'ended';

      if (isPlayed) {
        const hg = Number(m.homeGoals || 0);
        const ag = Number(m.awayGoals || 0);

        tableMap[homeId].matchesPlayed += 1;
        tableMap[awayId].matchesPlayed += 1;

        tableMap[homeId].goalsScored += hg;
        tableMap[homeId].goalsConceded += ag;
        tableMap[awayId].goalsScored += ag;
        tableMap[awayId].goalsConceded += hg;

        if (hg > ag) {
          tableMap[homeId].points += 3;
        } else if (ag > hg) {
          tableMap[awayId].points += 3;
        } else {
          tableMap[homeId].points += 1;
          tableMap[awayId].points += 1;
        }
      }
    });

    const baseTable = Object.values(tableMap);

    // 2. Grupowanie drużyn po liczbie punktów (H2H)
    const teamsByPoints = {};
    baseTable.forEach(team => {
      if (!teamsByPoints[team.points]) {
        teamsByPoints[team.points] = [];
      }
      teamsByPoints[team.points].push(team);
    });

    const finalTable = [];
    const sortedPoints = Object.keys(teamsByPoints).map(Number).sort((a, b) => b - a);

    // 3. Budowanie końcowej tabeli z uwzględnieniem "Małej Tabeli"
    sortedPoints.forEach(pts => {
      const tiedTeams = teamsByPoints[pts];

      if (tiedTeams.length === 1) {
        finalTable.push(tiedTeams[0]);
      } else {
        const tiedTeamIds = tiedTeams.map(t => t.teamId);
        const miniTableMap = {};
        
        tiedTeamIds.forEach(id => {
          miniTableMap[id] = { points: 0, goalsScored: 0, goalsConceded: 0 };
        });

        filteredMatches.forEach(m => {
          const isPlayed = (m.status || '').toLowerCase() === 'ended';
          
          if (isPlayed && tiedTeamIds.includes(m.homeTeamId) && tiedTeamIds.includes(m.awayTeamId)) {
             const hg = Number(m.homeGoals || 0);
             const ag = Number(m.awayGoals || 0);

             miniTableMap[m.homeTeamId].goalsScored += hg;
             miniTableMap[m.homeTeamId].goalsConceded += ag;
             miniTableMap[m.awayTeamId].goalsScored += ag;
             miniTableMap[m.awayTeamId].goalsConceded += hg;

             if (hg > ag) {
               miniTableMap[m.homeTeamId].points += 3;
             } else if (ag > hg) {
               miniTableMap[m.awayTeamId].points += 3;
             } else {
               miniTableMap[m.homeTeamId].points += 1;
               miniTableMap[m.awayTeamId].points += 1;
             }
          }
        });

        tiedTeams.sort((a, b) => {
          const miniA = miniTableMap[a.teamId];
          const miniB = miniTableMap[b.teamId];

          if (miniB.points !== miniA.points) return miniB.points - miniA.points;

          const miniGdA = miniA.goalsScored - miniA.goalsConceded;
          const miniGdB = miniB.goalsScored - miniB.goalsConceded;
          if (miniGdB !== miniGdA) return miniGdB - miniGdA;

          if (miniB.goalsScored !== miniA.goalsScored) return miniB.goalsScored - miniA.goalsScored;

          const overallGdA = a.goalsScored - a.goalsConceded;
          const overallGdB = b.goalsScored - b.goalsConceded;
          if (overallGdB !== overallGdA) return overallGdB - overallGdA;

          return b.goalsScored - a.goalsScored;
        });

        finalTable.push(...tiedTeams);
      }
    });

    return finalTable;
  }, [matches, selectedGroup]);
  
  const groupMatches = useMemo(() => {
  if (!matches) return [];
  return matches
    .filter(m => {
      const mRound = m.round ? m.round.toString().toUpperCase() : '';
      const mGroup = m.group ? m.group.toString().toUpperCase() : '';
      const mGroupName = m.groupName ? m.groupName.toString().toUpperCase() : '';
      const mStage = m.stage ? m.stage.toString().toUpperCase() : '';
      const targetGroupStr = `GRUPA ${selectedGroup}`;

      return (
        mRound === targetGroupStr || mRound === selectedGroup || mRound.endsWith(` ${selectedGroup}`) ||
        mGroup === targetGroupStr || mGroup === selectedGroup || mGroup.endsWith(` ${selectedGroup}`) ||
        mGroupName === targetGroupStr || mGroupName === selectedGroup || mGroupName.endsWith(` ${selectedGroup}`) ||
        mStage === targetGroupStr || mStage === selectedGroup || mStage.endsWith(` ${selectedGroup}`)
      );
    })
    // Sortowanie chronologiczne według daty/czasu meczu
    .sort((a, b) => new Date(a.date || a.matchDate || 0) - new Date(b.date || b.matchDate || 0));
}, [matches, selectedGroup]);
  
  const fullLeagueName = 'Mistrzostwa Świata';

  return (
    <>
      <Helmet>
        <title>{`${fullLeagueName} - Symulator i tabela | SymuLiga`}</title>
        <meta 
          name="description" 
          content={`${fullLeagueName} table simulator. Sprawdź szanse na mistrzostwo, awans i spadek. Unikalny kalkulator wyników końcowych oparty o ranking ELO.`} 
        />
        <link rel="canonical" href={`https://symuliga.pl/liga/${id}`} />
        <meta property="og:title" content={`${fullLeagueName} symulator - Symulacja tabeli`} />
        <meta property="og:description" content="Predykcje polskich lig piłkarskich. Sprawdź szanse swojej drużyny!" />
      </Helmet>

      <Box sx={{ textAlign: 'center', padding: { xs: '5px', md: '10px' } }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          {fullLeagueName}
        </Typography>
        
        <Typography variant="subtitle1" component="h2" sx={{ mb: 3, color: 'text.secondary' }}>
          Symulacja tabeli i szanse drużyn – kalkulator wyników końcowych
        </Typography>

        {/* Kafelki */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {mockTiles.map((tile) => (
            <Grid item xs={12} sm={6} md={6} key={tile.title}>
              <Link to={typeof tile.link === 'function' ? tile.link(id) : tile.link} style={{ textDecoration: 'none' }}>
                <Card
                  sx={{
                    backgroundColor: '#2c2c2c',
                    color: '#ffffff',
                    transition: 'background-color 0.3s',
                    '&:hover': { backgroundColor: '#3a3a3a' },
                    cursor: 'pointer',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: '16px' }}>
                    <Typography variant="h6">{tile.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tile.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Link>
            </Grid>
          ))}
        </Grid>
	  
        {/* Sekcja wyboru grupy (Kafelki A-L) */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', borderBottom: '2px solid #3a3a3a', pb: 1 }}>
          Tabele grupowe fazy zasadniczej
        </Typography>
        
        <Grid container spacing={1} sx={{ mb: 4 }}>
          {GROUPS.map((group) => {
            const isSelected = selectedGroup === group;
            return (
              <Grid item xs={3} sm={2} md={1} key={group}>
                <Card
                  onClick={() => setSelectedGroup(group)}
                  sx={{
                    backgroundColor: isSelected ? '#1976d2' : '#2c2c2c',
                    color: '#ffffff',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: isSelected ? '1px solid #ffffff' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: isSelected ? '#115293' : '#3a3a3a',
                    },
                  }}
                >
                  <CardContent sx={{ padding: '12px !important' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {group}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', fontWeight: 'bold' }}>
          Aktualna Sytuacja: Grupa {selectedGroup}
        </Typography>

        {groupTable.length === 0 ? (
  <Paper sx={{ p: 4, backgroundColor: '#2c2c2c', textAlign: 'center' }}>
    <Typography sx={{ color: '#aaa' }}>
      Brak rozegranych meczów lub danych dla grupy {selectedGroup} w tym turnieju.
    </Typography>
  </Paper>
) : (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
    
    {/* Kontener tabeli z dopasowaną szerokością i wyśrodkowaniem na stronie */}
    <TableContainer 
      component={Paper} 
      sx={{ 
        backgroundColor: '#2c2c2c', 
        borderRadius: '8px', 
        boxShadow: 3,
        width: 'auto',          // Szerokość dopasowana do zawartości
        maxWidth: '100%',       // Zabezpieczenie na mniejszych ekranach
        overflowX: 'auto',
        mb: 4                   // Margines dolny przed listą meczów
      }}
    >
      <Table sx={{ width: 'auto', minWidth: { xs: '320px', sm: '500px' } }}>
        <TableHead sx={{ backgroundColor: '#383838' }}>
          <TableRow>
            {/* Wyśrodkowana pierwsza kolumna nagłówka */}
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', width: '60px', textAlign: 'center' }}>Poz</TableCell>
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>Drużyna</TableCell>
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>M</TableCell>
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '80px' }}>Pkt</TableCell>
            <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', textAlign: 'center', width: '120px' }}>Bramki</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupTable.map((teamRow, index) => {
            // Logika kolorowania: 1 i 2 miejsce - mocniejszy zielony, 3 miejsce - słabszy zielony
            let rowBgColor = 'transparent';
            let hoverBgColor = '#363636';

            if (index < 2) {
              rowBgColor = 'rgba(76, 175, 80, 0.25)'; // Bardziej intensywny zielony
              hoverBgColor = 'rgba(76, 175, 80, 0.35)';
            } else if (index === 2) {
              rowBgColor = 'rgba(76, 175, 80, 0.08)'; // Mniej intensywny zielony
              hoverBgColor = 'rgba(76, 175, 80, 0.16)';
            }

            return (
              <TableRow
                key={teamRow.teamId}
                sx={{
                  backgroundColor: rowBgColor,
                  '&:hover': { backgroundColor: hoverBgColor },
                  transition: 'background-color 0.2s'
                }}
              >
                {/* Wyśrodkowana pierwsza kolumna body */}
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', fontWeight: 'bold', textAlign: 'center' }}>
                  {index + 1}
                </TableCell>
                <TableCell
                  sx={{
                    color: '#ffffff',
                    borderBottom: '1px solid #3a3a3a',
                    textDecoration: 'none',
                    fontWeight: index < 3 ? '500' : 'normal',
                   
                  }}
                >
                  {teamRow.teamName}
                </TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center' }}>
                  {teamRow.matchesPlayed}
                </TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center', fontWeight: 'bold' }}>
                  {teamRow.points}
                </TableCell>
                <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #3a3a3a', textAlign: 'center' }}>
                  {teamRow.goalsScored}:{teamRow.goalsConceded}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>

    {/* ================= SEKCJA: TERMINARZ I WYNIKI GRUPY ================= */}
    <Box sx={{ width: '100%', maxWidth: '600px', mt: 2, textAlign: 'left' }}>
      <Typography variant="h6" sx={{ color: '#ffffff', mb: 2, fontWeight: 'bold', borderBottom: '1px solid #3a3a3a', pb: 1, textAlign: 'center' }}>
        Terminarz i Wyniki - Grupa {selectedGroup}
      </Typography>
      
      {groupMatches.length === 0 ? (
        <Typography sx={{ color: '#aaa', textAlign: 'center', italic: 'true' }}>
          Brak zaplanowanych spotkań dla tej grupy.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {groupMatches.map((match) => {
            const isPlayed = (match.status || '').toLowerCase() === 'ended';
            const homeName = match.homeTeamName || (match.homeTeam && match.homeTeam.name) || `Drużyna ${match.homeTeamId}`;
            const awayName = match.awayTeamName || (match.awayTeam && match.awayTeam.name) || `Drużyna ${match.awayTeamId}`;
            
            // Formatowanie ładnej daty (jeśli istnieje)
            let formattedDate = '';
            if (match.date || match.matchDate) {
              const d = new Date(match.date || match.matchDate);
              formattedDate = d.toLocaleString('pl-PL', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            }

            return (
              <Paper 
                key={match.id || match.matchId || `${match.homeTeamId}-${match.awayTeamId}`}
                sx={{ 
                  p: 1.5, 
                  backgroundColor: '#222222', 
                  borderLeft: isPlayed ? '4px solid #1976d2' : '4px solid #ff9800',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                {/* Data meczu */}
                <Typography variant="caption" sx={{ color: '#888888', minWidth: '75px' }}>
                  {formattedDate}
                </Typography>

                {/* Drużyny i Wynik */}
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: '500', color: '#ffffff', textAlign: 'center', flex: 1 }}>
                    {homeName}
                  </Typography>
                  
                  {/* Kontener wyniku / statusu */}
                  <Box 
                    sx={{ 
                      backgroundColor: '#2c2c2c', 
                      px: 1.5, 
                      py: 0.5, 
                      borderRadius: '4px', 
                      minWidth: '50px', 
                      textAlign: 'center',
                      border: '1px solid #3a3a3a'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: isPlayed ? '#ff9800' : '#888888' }}>
                      {isPlayed ? `${match.homeGoals}:${match.awayGoals}` : 'vs'}
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: '500', color: '#ffffff', textAlign: 'center', flex: 1 }}>
                    {awayName}
                  </Typography>
                </Box>

                
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
    {/* ================= KONIEC SEKCJI MECZÓW ================= */}

  </Box>
)}
      </Box>
    </>
  );
};

export default WcLeaguePage;